import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  initializeAuth,
  logout,
  selectCurrentToken,
  selectIsInitialized,
  selectIsAuthenticated,
  setVerifiedUser,
} from '../features/auth/authSlice';
import { useLazyProfileQuery } from '../features/auth/api/authApiSlice';
import type { RootState } from '../store/store';

interface AuthInitializerProps {
  children: React.ReactNode;
}

export const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => selectCurrentToken(state));
  const isInitialized = useSelector((state: RootState) => selectIsInitialized(state));
  const isAuthenticated = useSelector((state: RootState) => selectIsAuthenticated(state));
  const [loadProfile] = useLazyProfileQuery();
  const verifiedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (isInitialized) {
      verifiedTokenRef.current = null;
      return;
    }

    if (!token) {
      dispatch(initializeAuth());
      return;
    }

    if (verifiedTokenRef.current === token) return;
    verifiedTokenRef.current = token;

    // Do not skip/cancel on isFetching: that left initialized=false after a 401
    // and retried /profile forever (spinner + console loop).
    void loadProfile()
      .unwrap()
      .then(result => {
        dispatch(setVerifiedUser(result.user));
      })
      .catch(() => {
        dispatch(logout());
      });
  }, [dispatch, isInitialized, loadProfile, token]);

  useEffect(() => {
    const publicRoutes = ['/login', '/chatbot', '/forgot-password', '/reset-password'];
    const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

    if (isInitialized && !isAuthenticated && !isPublicRoute) {
      navigate('/login', {
        replace: true,
        state: { from: location },
      });
    }
  }, [isInitialized, isAuthenticated, location, navigate]);

  if (!isInitialized) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900'></div>
      </div>
    );
  }

  return <>{children}</>;
};
